import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Input } from "./Field";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  /** Currently selected values. Empty = nothing selected ("all"). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Trigger label shown when nothing is selected, e.g. "All families". */
  placeholder?: string;
  /** Accessible label for the trigger. */
  "aria-label"?: string;
  /** Show a search box above the list — useful for long option sets. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Message when the (filtered) option list is empty. */
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  /** Forwarded to the wrapper — set `width` here to size the control like a `<Select>`. */
  style?: CSSProperties;
}

/**
 * Checkbox multi-select rendered as a popover, styled to line up with `<Select>`
 * in a toolbar. Empty selection means "all" (no filtering). Closes on click-outside
 * or Escape. Pass `searchable` for long lists (e.g. currencies, tiers).
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "All",
  searchable = false,
  searchPlaceholder = "Search…",
  emptyText = "No options",
  disabled = false,
  className,
  style,
  ...rest
}: MultiSelectProps) {
  const ariaLabel = rest["aria-label"];
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Clear the search filter whenever the popover closes so it reopens clean.
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const selected = useMemo(() => new Set(value), [value]);
  const selectedLabels = useMemo(
    () => options.filter((o) => selected.has(o.value)).map((o) => o.label),
    [options, selected]
  );
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, q]);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  };

  // Compact summary: one label, or "first +N" for several, full set in the tooltip.
  const summary =
    selectedLabels.length === 0
      ? null
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  return (
    <div ref={ref} className={["fd-multiselect", className].filter(Boolean).join(" ")} style={style}>
      <button
        type="button"
        className={["fd-multiselect__trigger", selected.size > 0 && "fd-multiselect__trigger--active"].filter(Boolean).join(" ")}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || options.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={summary ? selectedLabels.join(", ") : undefined}
      >
        {summary ?? <span className="fd-multiselect__placeholder">{placeholder}</span>}
      </button>
      {open && (
        <div className="fd-multiselect__panel" role="listbox" aria-multiselectable="true" aria-label={ariaLabel} id={listId}>
          {searchable && (
            <div className="fd-multiselect__search">
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} />
            </div>
          )}
          <div className="fd-multiselect__list">
            {list.length === 0 ? (
              <div className="fd-multiselect__empty">{emptyText}</div>
            ) : (
              list.map((o) => (
                <label key={o.value} className="fd-multiselect__option" role="option" aria-selected={selected.has(o.value)}>
                  <input
                    type="checkbox"
                    className="fd-checkbox"
                    checked={selected.has(o.value)}
                    onChange={() => toggle(o.value)}
                  />
                  <span className="fd-multiselect__option-label" title={o.label}>{o.label}</span>
                </label>
              ))
            )}
          </div>
          {selected.size > 0 && (
            <div className="fd-multiselect__footer">
              <button type="button" className="fd-multiselect__clear" onClick={() => onChange([])}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
